import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, ExternalLink, Link2, RefreshCw, Globe, Upload, ImageIcon, GripVertical, ChevronUp, ChevronDown, Star, Clock, Eye, EyeOff, BarChart3, Smartphone, Tablet, Monitor, X } from 'lucide-react';
import { FaPlay, FaFileAlt, FaDownload, FaExternalLinkAlt, FaLinkedin, FaInstagram, FaTwitter, FaFacebook, FaSpotify, FaTiktok, FaYoutube, FaGlobe, FaEnvelope, FaLink, FaBookOpen, FaCalendarAlt, FaMapMarkerAlt, FaShoppingCart, FaUsers, FaMicrophone, FaNewspaper, FaLock, FaImage, FaHeadphones, FaPodcast, FaBuilding } from 'react-icons/fa';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';
import { LinkHubRenderer } from './LinkHub';
import './LinkHub.css';
import './AdminLinkHubPreview.css';

const LINK_ICONS = [
  { value: 'auto', label: 'Auto-detect', icon: FaLink },
  { value: 'external', label: 'External Link', icon: FaExternalLinkAlt },
  { value: 'play', label: 'Play / Video', icon: FaPlay },
  { value: 'download', label: 'Download', icon: FaDownload },
  { value: 'file', label: 'Document', icon: FaFileAlt },
  { value: 'book', label: 'Read / Book', icon: FaBookOpen },
  { value: 'newspaper', label: 'Article / News', icon: FaNewspaper },
  { value: 'globe', label: 'Website', icon: FaGlobe },
  { value: 'calendar', label: 'Event / Date', icon: FaCalendarAlt },
  { value: 'map', label: 'Location', icon: FaMapMarkerAlt },
  { value: 'cart', label: 'Shop / Store', icon: FaShoppingCart },
  { value: 'users', label: 'Community', icon: FaUsers },
  { value: 'mic', label: 'Podcast / Audio', icon: FaMicrophone },
  { value: 'headphones', label: 'Listen', icon: FaHeadphones },
  { value: 'lock', label: 'Gated / Private', icon: FaLock },
  { value: 'image', label: 'Gallery / Photo', icon: FaImage },
  { value: 'building', label: 'Company', icon: FaBuilding },
  { value: 'email', label: 'Email', icon: FaEnvelope },
  { value: 'linkedin', label: 'LinkedIn', icon: FaLinkedin },
  { value: 'instagram', label: 'Instagram', icon: FaInstagram },
  { value: 'twitter', label: 'X / Twitter', icon: FaTwitter },
  { value: 'facebook', label: 'Facebook', icon: FaFacebook },
  { value: 'youtube', label: 'YouTube', icon: FaYoutube },
  { value: 'spotify', label: 'Spotify', icon: FaSpotify },
  { value: 'tiktok', label: 'TikTok', icon: FaTiktok },
];

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem('admin_token');
const auth = () => ({ Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' });

const PRESETS = {
  wnc_navy: { label: 'WNC Navy', preset: 'wnc_navy', theme_mode: 'dark', background_type: 'gradient', gradient_start: '#001428', gradient_end: '#00264d', card_background: 'rgba(0, 20, 40, 0.8)', accent_color: '#d4a853', accent_bright: '#f0c96e', title_color: '#ffffff', bio_color: '#cbd5e1', link_title_color: '#ffffff', link_subtitle_color: '#94a3b8', button_style: 'glass', show_grain_texture: true, show_social_row: true, card_spacing: 16, badge_bg_color: '#d4a853', badge_text_color: '#001428' },
  emerald_dark: { label: 'Emerald Dark', preset: 'emerald_dark', theme_mode: 'dark', background_type: 'gradient', gradient_start: '#0d3028', gradient_end: '#050f0c', card_background: 'rgba(15, 38, 32, 0.75)', accent_color: '#004534', accent_bright: '#00d99a', title_color: '#F9FAFB', bio_color: '#A3B3B3', link_title_color: '#F9FAFB', link_subtitle_color: '#9CA3AF', button_style: 'glass', show_grain_texture: true, show_social_row: true, card_spacing: 16, badge_bg_color: '#fbbf24', badge_text_color: '#1f2937' },
  light_mode: { label: 'Light', preset: 'light_mode', theme_mode: 'light', background_type: 'gradient', gradient_start: '#f0fdf9', gradient_end: '#ffffff', card_background: 'rgba(0, 69, 52, 0.08)', accent_color: '#004534', accent_bright: '#00d99a', title_color: '#111827', bio_color: '#6b7280', link_title_color: '#111827', link_subtitle_color: '#6b7280', button_style: 'glass', show_grain_texture: false, show_social_row: true, card_spacing: 16, badge_bg_color: '#fbbf24', badge_text_color: '#1f2937' },
  neutral_gray: { label: 'Slate', preset: 'neutral_gray', theme_mode: 'dark', background_type: 'gradient', gradient_start: '#1f2937', gradient_end: '#111827', card_background: 'rgba(31, 41, 55, 0.8)', accent_color: '#6366f1', accent_bright: '#818cf8', title_color: '#F9FAFB', bio_color: '#9CA3AF', link_title_color: '#F9FAFB', link_subtitle_color: '#9CA3AF', button_style: 'glass', show_grain_texture: true, show_social_row: true, card_spacing: 16, badge_bg_color: '#fbbf24', badge_text_color: '#1f2937' },
};

const SOCIALS = [
  { v: 'instagram', l: 'Instagram' }, { v: 'twitter', l: 'X / Twitter' }, { v: 'youtube', l: 'YouTube' },
  { v: 'facebook', l: 'Facebook' }, { v: 'linkedin', l: 'LinkedIn' }, { v: 'tiktok', l: 'TikTok' },
  { v: 'spotify', l: 'Spotify' }, { v: 'apple', l: 'Apple Podcasts' }, { v: 'threads', l: 'Threads' },
  { v: 'email', l: 'Email' }, { v: 'website', l: 'Website' },
];

const newHub = () => ({
  title: '', slug: '', published: true, noindex: true, footer_text: '', utm_params: '',
  header: { display_name: '', bio: '', avatar_image: '', banner_image: '', show_banner: true, logos: [] },
  theme: { ...PRESETS.wnc_navy }, links: [], social_links: [],
  contacts: [], contacts_heading: 'Contacts', contacts_position: 'bottom', contacts_layout: 'grid',
  blocks: [],
});

const newLink = () => ({ id: crypto.randomUUID(), label: '', url: '', subtext: '', featured: false, open_in_new_tab: true, enabled: true, coming_soon: false, is_gated: false, resource_url: '', gate_headline: '', gate_description: '', email_subject: '', email_body: '', add_to_list_id: '' });
const newSocial = () => ({ id: crypto.randomUUID(), platform: 'instagram', url: '', enabled: true });
const newContact = () => ({ id: crypto.randomUUID(), name: '', role: '', email: '', phone: '', linkedin: '', photo: '', enabled: true });
const newBlockNewsletter = () => ({ id: crypto.randomUUID(), type: 'newsletter', enabled: true, position: 'bottom', headline: 'Stay in the loop', description: 'Get updates on the World Nuclear Congress delivered to your inbox.', button_label: 'Subscribe', success_message: "You're in — thanks for signing up.", add_to_list_id: '' });
const newBlockHtml = () => ({ id: crypto.randomUUID(), type: 'html', enabled: true, position: 'bottom', html: '<p style="text-align:center;color:#888">Custom HTML block</p>' });

// ===== Reusable image uploader =====
const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('folder', 'link-hub');
  const res = await fetch(`${API}/api/media/upload`, { method: 'POST', body: fd });
  if (!res.ok) throw new Error('Upload failed');
  return (await res.json()).url;
};

// ===== Image Upload Zone =====
const ImgUpload = ({ value, onChange, label, shape = 'rect', hint }) => {
  const [busy, setBusy] = useState(false);
  const imgSrc = value?.startsWith('/') ? `${API}${value}` : value;
  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { onChange(await uploadImage(file)); toast.success(`${label} uploaded`); }
    catch { toast.error('Upload failed'); }
    setBusy(false);
  };
  const isCircle = shape === 'circle';

  if (value) {
    return (
      <div className="relative group">
        <img src={imgSrc} alt="" className={`object-cover border bg-gray-50 ${isCircle ? 'w-20 h-20 rounded-full' : 'w-full h-32 rounded-lg'}`} />
        <div className={`absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 ${isCircle ? 'rounded-full' : 'rounded-lg'}`}>
          <label className="bg-white/90 rounded px-2 py-1 text-xs font-medium cursor-pointer">
            Replace <input type="file" accept="image/*" className="hidden" onChange={e => doUpload(e.target.files[0])} />
          </label>
          <button onClick={() => onChange('')} className="bg-white/90 rounded px-2 py-1 text-xs font-medium text-red-600">Remove</button>
        </div>
      </div>
    );
  }
  return (
    <label className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors ${isCircle ? 'w-20 h-20 rounded-full' : 'w-full h-24'}`}>
      {busy ? <span className="text-xs text-gray-400">Uploading...</span> : (
        <><ImageIcon className="w-5 h-5 text-gray-400 mb-0.5" /><span className="text-[11px] text-gray-500">{hint || label}</span></>
      )}
      <input type="file" accept="image/*" className="hidden" onChange={e => doUpload(e.target.files[0])} />
    </label>
  );
};

// ===== Color Picker =====
const ColorPick = ({ label, value, onChange }) => (
  <div>
    <Label className="text-xs mb-1 block">{label}</Label>
    <div className="flex gap-1.5 items-center">
      <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)} className="w-7 h-7 rounded cursor-pointer border-0 p-0" />
      <Input className="text-xs h-8" value={value || ''} onChange={e => onChange(e.target.value)} />
    </div>
  </div>
);

// ===== MAIN PAGE =====
const AdminLinkHub = () => {
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState('content');
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('mobile');
  const [dragItemId, setDragItemId] = useState(null);
  const [dragKind, setDragKind] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [contactLists, setContactLists] = useState([]);
  const [expandedLinkId, setExpandedLinkId] = useState(null);

  const fetch_hubs = useCallback(async () => {
    try { const r = await fetch(`${API}/api/link-hub/admin/pages`, { headers: auth() }); if (r.ok) setHubs(await r.json()); }
    catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetch_hubs(); }, [fetch_hubs]);

  // Load reusable contact lists for gate/newsletter dropdowns
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/email/lists`, { headers: auth() });
        if (r.ok) setContactLists(await r.json());
      } catch (e) { /* no-op */ }
    })();
  }, []);

  const set = (path, val) => {
    setEditing(prev => {
      const c = JSON.parse(JSON.stringify(prev));
      const k = path.split('.');
      let o = c;
      for (let i = 0; i < k.length - 1; i++) o = o[k[i]];
      o[k[k.length - 1]] = val;
      return c;
    });
  };

  const save = async () => {
    if (!editing.title?.trim()) return toast.error('Title required');
    if (!editing.slug?.trim()) return toast.error('Slug required');
    setSaving(true);
    try {
      const isNew = !editing.id;
      const r = await fetch(isNew ? `${API}/api/link-hub/pages` : `${API}/api/link-hub/pages/${editing.id}`, { method: isNew ? 'POST' : 'PUT', headers: auth(), body: JSON.stringify(editing) });
      if (r.ok) { toast.success(isNew ? 'Hub created!' : 'Saved!'); setEditing(null); fetch_hubs(); }
      else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Save failed'); }
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const del = async (hub) => {
    if (!window.confirm(`Delete "${hub.title}"?`)) return;
    try { const r = await fetch(`${API}/api/link-hub/pages/${hub.id}`, { method: 'DELETE', headers: auth() }); if (r.ok) { toast.success('Deleted'); fetch_hubs(); } }
    catch { toast.error('Delete failed'); }
  };

  // Link helpers
  const addLink = () => set('links', [...(editing.links || []), newLink()]);
  const rmLink = (id) => set('links', editing.links.filter(l => l.id !== id));
  const updLink = (id, f, v) => set('links', editing.links.map(l => l.id === id ? { ...l, [f]: v } : l));

  // Block helpers (newsletter / html embeds)
  const addBlock = (type) => set('blocks', [...(editing.blocks || []), type === 'html' ? newBlockHtml() : newBlockNewsletter()]);
  const rmBlock = (id) => set('blocks', (editing.blocks || []).filter(b => b.id !== id));
  const updBlock = (id, f, v) => set('blocks', (editing.blocks || []).map(b => b.id === id ? { ...b, [f]: v } : b));
  // Generic drag-and-drop reorder for any array-of-{id} field on `editing`
  const reorderField = (field, sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const arr = [...(editing[field] || [])];
    const s = arr.findIndex(x => x.id === sourceId);
    const t = arr.findIndex(x => x.id === targetId);
    if (s === -1 || t === -1) return;
    const [moved] = arr.splice(s, 1);
    arr.splice(t, 0, moved);
    set(field, arr);
  };
  const startDrag = (e, id, kind) => {
    setDragItemId(id); setDragKind(kind);
    try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', `${kind}:${id}`); } catch { /* ignore */ }
  };
  const dragOverIf = (e, id, kind) => {
    if (dragKind && dragKind !== kind) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch { /* ignore */ }
    if (dropTargetId !== id) setDropTargetId(id);
  };
  const dropIf = (e, id, kind, field) => {
    e.preventDefault();
    let src = dragItemId; let k = dragKind;
    try {
      const raw = e.dataTransfer.getData('text/plain');
      if (raw && raw.includes(':')) { const [rk, rid] = raw.split(':'); k = rk; src = rid; }
    } catch { /* ignore */ }
    if (k !== kind) { setDragItemId(null); setDragKind(null); setDropTargetId(null); return; }
    reorderField(field, src, id);
    setDragItemId(null); setDragKind(null); setDropTargetId(null);
  };
  const endDrag = () => { setDragItemId(null); setDragKind(null); setDropTargetId(null); };

  // Legacy per-link helpers (kept for backwards compat with the arrow buttons)
  const moveLink = (id, dir) => {
    const ls = [...editing.links]; const i = ls.findIndex(l => l.id === id); const s = dir === 'up' ? i - 1 : i + 1;
    if (s < 0 || s >= ls.length) return; [ls[i], ls[s]] = [ls[s], ls[i]]; set('links', ls);
  };

  // Logo upload
  const addLogo = async (file) => {
    if (!file) return;
    try { const url = await uploadImage(file); set('header.logos', [...(editing.header?.logos || []), { id: crypto.randomUUID(), url, alt: '', enabled: true, size: 52 }]); toast.success('Logo added'); }
    catch { toast.error('Upload failed'); }
  };

  // ===== EDITOR VIEW =====
  if (editing) {
    return (
      <AdminLayout>
        <div className={`admin-linkhub-editor-shell ${showPreview ? 'with-preview' : ''}`}>
          <div className="admin-linkhub-editor-pane">
            <div className="p-4 lg:p-6 max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{editing.id ? 'Edit Hub' : 'New Hub'}</h1>
              {editing.slug && <p className="text-xs text-gray-500 mt-0.5">/links/{editing.slug}</p>}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant={showPreview ? 'default' : 'outline'} onClick={() => setShowPreview(v => !v)} data-testid="toggle-preview-btn"
                className={showPreview ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}>
                {showPreview ? <EyeOff className="w-3.5 h-3.5 mr-1" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
                {showPreview ? 'Hide Preview' : 'Preview'}
              </Button>
              {editing.slug && <Button size="sm" variant="outline" onClick={() => window.open(`/links/${editing.slug}`, '_blank')}><ExternalLink className="w-3.5 h-3.5 mr-1" /> Open</Button>}
              <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="save-hub-btn" className="bg-blue-900 hover:bg-blue-800 text-white">
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-5"><TabsTrigger value="content">Content</TabsTrigger><TabsTrigger value="design">Design</TabsTrigger><TabsTrigger value="settings">Settings</TabsTrigger></TabsList>

            {/* ====== CONTENT ====== */}
            <TabsContent value="content" className="space-y-5">
              {/* Identity */}
              <section className="bg-white rounded-xl border p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Identity</h2>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5">
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">Page Title *</Label><Input value={editing.title} onChange={e => set('title', e.target.value)} placeholder="My Hub" data-testid="hub-title-input" /></div>
                      <div><Label className="text-xs">URL Slug *</Label><div className="flex items-center"><span className="text-xs text-gray-400 mr-1">/links/</span><Input value={editing.slug} onChange={e => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} placeholder="my-hub" data-testid="hub-slug-input" /></div></div>
                    </div>
                    <div><Label className="text-xs">Display Name</Label><Input value={editing.header?.display_name || ''} onChange={e => set('header.display_name', e.target.value)} /></div>
                    <div><Label className="text-xs">Bio</Label><textarea className="w-full border rounded-lg p-2 text-sm resize-none" rows={2} value={editing.header?.bio || ''} onChange={e => set('header.bio', e.target.value)} placeholder="Short tagline..." /></div>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-xs text-gray-500">Profile Photo</Label>
                      <Switch checked={editing.header?.show_avatar !== false} onCheckedChange={v => set('header.show_avatar', v)} />
                    </div>
                    <ImgUpload value={editing.header?.avatar_image} onChange={v => set('header.avatar_image', v)} label="Profile" shape="circle" hint="Photo" />
                  </div>
                </div>
              </section>

              {/* Banner & Logos */}
              <section className="bg-white rounded-xl border p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Banner & Logos</h2>
                  <div className="flex items-center gap-2"><Switch checked={editing.header?.show_banner !== false} onCheckedChange={v => set('header.show_banner', v)} /><span className="text-xs text-gray-500">Show banner</span></div>
                </div>
                <ImgUpload value={editing.header?.banner_image} onChange={v => set('header.banner_image', v)} label="Banner" hint="Upload banner (1200x300)" />
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs">Logos</Label>
                    <label className="text-xs text-blue-600 cursor-pointer hover:text-blue-700 font-medium flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Add
                      <input type="file" accept="image/*" className="hidden" onChange={e => { addLogo(e.target.files[0]); e.target.value = ''; }} data-testid="add-logo-btn" />
                    </label>
                  </div>
                  {(editing.header?.logos || []).length === 0 ? (
                    <label className="flex items-center justify-center w-full py-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors">
                      <ImageIcon className="w-4 h-4 text-gray-400 mr-2" /><span className="text-xs text-gray-500">Upload logos</span>
                      <input type="file" accept="image/*" className="hidden" onChange={e => { addLogo(e.target.files[0]); e.target.value = ''; }} />
                    </label>
                  ) : (
                    <div className="flex flex-wrap gap-3">
                      {editing.header.logos.map((logo) => (
                        <div key={logo.id} className="relative group flex flex-col items-center gap-1" style={{ width: Math.max(60, (logo.size || 52) + 16) }}>
                          <div className="relative">
                            <img src={logo.url.startsWith('/') ? `${API}${logo.url}` : logo.url} alt=""
                              style={{ height: `${logo.size || 52}px` }}
                              className={`w-auto rounded border bg-white p-1 object-contain transition-all ${logo.enabled === false ? 'opacity-30' : ''}`} />
                            <button onClick={() => set('header.logos', editing.header.logos.filter(l => l.id !== logo.id))} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">x</button>
                          </div>
                          <input type="range" min="24" max="120" value={logo.size || 52}
                            onChange={e => set('header.logos', editing.header.logos.map(l => l.id === logo.id ? { ...l, size: parseInt(e.target.value) } : l))}
                            className="w-full h-1 accent-blue-500 cursor-pointer" title={`${logo.size || 52}px`} />
                          <button onClick={() => set('header.logos', editing.header.logos.map(l => l.id === logo.id ? { ...l, enabled: l.enabled === false ? true : false } : l))}
                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${logo.enabled === false ? 'bg-gray-200 text-gray-500' : 'bg-green-100 text-green-700'}`}>
                            {logo.enabled === false ? 'Off' : 'On'}
                          </button>
                        </div>
                      ))}
                      <label className="h-14 w-14 rounded border-2 border-dashed flex items-center justify-center cursor-pointer hover:border-blue-400 transition-colors">
                        <Plus className="w-4 h-4 text-gray-400" />
                        <input type="file" accept="image/*" className="hidden" onChange={e => { addLogo(e.target.files[0]); e.target.value = ''; }} />
                      </label>
                    </div>
                  )}
                </div>
              </section>

              {/* Links */}
              <section className="bg-white rounded-xl border p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Links ({editing.links?.length || 0})</h2>
                  <Button size="sm" variant="outline" onClick={addLink} data-testid="add-link-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                </div>
                {editing.links?.length === 0 && <p className="text-xs text-gray-400 text-center py-6">No links. Click Add to start.</p>}
                <div className="space-y-2">
                  {editing.links?.map((link, idx) => (
                    <div
                      key={link.id}
                      onDragOver={(e) => dragOverIf(e, link.id, 'link')}
                      onDrop={(e) => dropIf(e, link.id, 'link', 'links')}
                      onDragLeave={() => { if (dropTargetId === link.id) setDropTargetId(null); }}
                      data-testid={`link-row-${idx}`}
                      className={`border rounded-lg p-3 bg-gray-50/80 transition-all ${!link.enabled ? 'opacity-50' : ''} ${dragItemId === link.id && dragKind === 'link' ? 'opacity-40 scale-[0.99]' : ''} ${dropTargetId === link.id && dragKind === 'link' && dragItemId && dragItemId !== link.id ? 'ring-2 ring-blue-400 border-blue-300 bg-blue-50/60' : ''}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="flex flex-col items-center"
                          draggable
                          onDragStart={(e) => startDrag(e, link.id, 'link')}
                          onDragEnd={endDrag}
                          title="Drag to reorder"
                          data-testid={`link-drag-handle-${idx}`}
                        >
                          <GripVertical className="w-4 h-4 text-gray-300 hover:text-gray-600 cursor-grab active:cursor-grabbing mb-0.5" />
                          <button onClick={() => moveLink(link.id, 'up')} disabled={idx === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-20"><ChevronUp className="w-3 h-3" /></button>
                          <button onClick={() => moveLink(link.id, 'down')} disabled={idx === editing.links.length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-20"><ChevronDown className="w-3 h-3" /></button>
                        </div>
                        {/* Icon Selector */}
                        <div className="relative group/icon">
                          <button className="w-8 h-8 rounded-lg border bg-white flex items-center justify-center text-gray-600 hover:border-blue-400 transition-colors" title="Choose icon">
                            {(() => { const ic = LINK_ICONS.find(i => i.value === (link.icon || 'auto')); const Ic = ic?.icon || FaLink; return <Ic className="w-3.5 h-3.5" />; })()}
                          </button>
                          <div className="absolute left-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 hidden group-hover/icon:grid grid-cols-5 gap-0.5 p-1.5 w-[200px]">
                            {LINK_ICONS.map(opt => {
                              const OptIcon = opt.icon;
                              return <button key={opt.value} onClick={() => updLink(link.id, 'icon', opt.value)} title={opt.label}
                                className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${link.icon === opt.value || (!link.icon && opt.value === 'auto') ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-600'}`}>
                                <OptIcon className="w-3.5 h-3.5" />
                              </button>;
                            })}
                          </div>
                        </div>
                        <Input className="flex-1 h-8 text-sm font-medium" value={link.label} onChange={e => updLink(link.id, 'label', e.target.value)} placeholder="Label" />
                        <button onClick={() => updLink(link.id, 'featured', !link.featured)} className={`p-1 rounded ${link.featured ? 'text-yellow-500' : 'text-gray-300 hover:text-gray-500'}`} title="Featured"><Star className="w-4 h-4" fill={link.featured ? 'currentColor' : 'none'} /></button>
                        <button onClick={() => updLink(link.id, 'coming_soon', !link.coming_soon)} className={`p-1 rounded ${link.coming_soon ? 'text-orange-500' : 'text-gray-300 hover:text-gray-500'}`} title="Coming Soon"><Clock className="w-4 h-4" /></button>
                        <button onClick={() => { updLink(link.id, 'is_gated', !link.is_gated); if (!link.is_gated) setExpandedLinkId(link.id); }} className={`p-1 rounded ${link.is_gated ? 'text-blue-600' : 'text-gray-300 hover:text-gray-500'}`} title="Gated resource (email capture)" data-testid={`toggle-gate-${idx}`}><FaLock className="w-3.5 h-3.5" /></button>
                        <button onClick={() => updLink(link.id, 'enabled', !link.enabled)} className={`p-1 rounded ${link.enabled ? 'text-green-500' : 'text-gray-300'}`} title="Enabled">{link.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                        <button onClick={() => rmLink(link.id)} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="flex gap-2 pl-6">
                        <Input className="flex-1 h-7 text-xs" value={link.url} onChange={e => updLink(link.id, 'url', e.target.value)} placeholder="https://..." />
                        <Input className="w-1/3 h-7 text-xs" value={link.subtext || ''} onChange={e => updLink(link.id, 'subtext', e.target.value)} placeholder="Subtext" />
                      </div>

                      {/* Gate config (collapsible) */}
                      {link.is_gated && (
                        <div className="mt-2 ml-6 pl-3 pr-3 py-3 bg-blue-50/70 border border-blue-200 rounded-md space-y-2" data-testid={`gate-config-${idx}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-900">
                              <FaLock className="w-3 h-3" /> Gated resource
                            </div>
                            <button onClick={() => setExpandedLinkId(expandedLinkId === link.id ? null : link.id)} className="text-[11px] text-blue-700 hover:underline">
                              {expandedLinkId === link.id ? 'Collapse' : 'Configure'}
                            </button>
                          </div>
                          {expandedLinkId === link.id && (
                            <div className="space-y-2 pt-1">
                              <div>
                                <Label className="text-[11px] text-gray-600">Resource URL (delivered after unlock)</Label>
                                <Input className="h-7 text-xs" value={link.resource_url || ''} onChange={e => updLink(link.id, 'resource_url', e.target.value)} placeholder="https://... (defaults to the link URL above)" data-testid={`gate-resource-url-${idx}`} />
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-[11px] text-gray-600">Modal headline</Label>
                                  <Input className="h-7 text-xs" value={link.gate_headline || ''} onChange={e => updLink(link.id, 'gate_headline', e.target.value)} placeholder="Unlock this resource" />
                                </div>
                                <div>
                                  <Label className="text-[11px] text-gray-600">Submit button label</Label>
                                  <Input className="h-7 text-xs" value={link.gate_submit_label || ''} onChange={e => updLink(link.id, 'gate_submit_label', e.target.value)} placeholder="Get access" />
                                </div>
                              </div>
                              <div>
                                <Label className="text-[11px] text-gray-600">Modal description</Label>
                                <Input className="h-7 text-xs" value={link.gate_description || ''} onChange={e => updLink(link.id, 'gate_description', e.target.value)} placeholder="Enter your email to receive the resource" />
                              </div>
                              <div>
                                <Label className="text-[11px] text-gray-600">Email subject line</Label>
                                <Input className="h-7 text-xs" value={link.email_subject || ''} onChange={e => updLink(link.id, 'email_subject', e.target.value)} placeholder="Your requested resource" data-testid={`gate-email-subject-${idx}`} />
                              </div>
                              <div>
                                <Label className="text-[11px] text-gray-600">Email HTML body <span className="text-gray-400">— supports {'{{first_name}}'}, {'{{resource_url}}'}, {'{{link_label}}'}</span></Label>
                                <textarea className="w-full text-xs border rounded p-2 font-mono" rows={4} value={link.email_body || ''} onChange={e => updLink(link.id, 'email_body', e.target.value)} placeholder="Leave blank for the default template" data-testid={`gate-email-body-${idx}`} />
                              </div>
                              <div>
                                <Label className="text-[11px] text-gray-600">Add unlockers to contact list (optional)</Label>
                                <select className="w-full h-7 text-xs border rounded px-2 bg-white" value={link.add_to_list_id || ''} onChange={e => updLink(link.id, 'add_to_list_id', e.target.value)} data-testid={`gate-list-${idx}`}>
                                  <option value="">— None —</option>
                                  {contactLists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.contact_count || 0})</option>)}
                                </select>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Social */}
              <section className="bg-white rounded-xl border p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Social ({editing.social_links?.length || 0})</h2>
                  <Button size="sm" variant="outline" onClick={() => set('social_links', [...(editing.social_links || []), newSocial()])}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                </div>
                <div className="space-y-2">
                  {editing.social_links?.map((s, idx) => (
                    <div
                      key={s.id}
                      onDragOver={(e) => dragOverIf(e, s.id, 'social')}
                      onDrop={(e) => dropIf(e, s.id, 'social', 'social_links')}
                      onDragLeave={() => { if (dropTargetId === s.id) setDropTargetId(null); }}
                      data-testid={`social-row-${idx}`}
                      className={`flex items-center gap-2 border rounded-lg p-1.5 transition-all ${dragItemId === s.id && dragKind === 'social' ? 'opacity-40 scale-[0.99]' : ''} ${dropTargetId === s.id && dragKind === 'social' && dragItemId && dragItemId !== s.id ? 'ring-2 ring-blue-400 border-blue-300 bg-blue-50/60' : 'border-transparent'}`}
                    >
                      <div
                        draggable
                        onDragStart={(e) => startDrag(e, s.id, 'social')}
                        onDragEnd={endDrag}
                        title="Drag to reorder"
                        data-testid={`social-drag-handle-${idx}`}
                        className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-600"
                      >
                        <GripVertical className="w-4 h-4" />
                      </div>
                      <select value={s.platform} onChange={e => set('social_links', editing.social_links.map(x => x.id === s.id ? { ...x, platform: e.target.value } : x))} className="border rounded px-2 py-1.5 text-xs bg-white w-32">
                        {SOCIALS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
                      </select>
                      <Input className="flex-1 h-8 text-xs" value={s.url} onChange={e => set('social_links', editing.social_links.map(x => x.id === s.id ? { ...x, url: e.target.value } : x))} placeholder="URL or email" />
                      <button onClick={() => set('social_links', editing.social_links.filter(x => x.id !== s.id))} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </section>

              {/* Contacts */}
              <section className="bg-white rounded-xl border p-5 space-y-3" data-testid="contacts-admin-section">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Contacts ({editing.contacts?.length || 0})</h2>
                  <Button size="sm" variant="outline" onClick={() => set('contacts', [...(editing.contacts || []), newContact()])} data-testid="add-contact-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Contact</Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pb-1">
                  <div>
                    <Label className="text-xs">Section Heading</Label>
                    <Input className="h-8 text-sm" value={editing.contacts_heading || ''} onChange={e => set('contacts_heading', e.target.value)} placeholder="Contacts" data-testid="contacts-heading-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Position</Label>
                    <select value={editing.contacts_position || 'bottom'} onChange={e => set('contacts_position', e.target.value)} className="w-full border rounded-lg px-2 h-8 text-sm bg-white" data-testid="contacts-position-select">
                      <option value="top">Top (above links)</option>
                      <option value="bottom">Bottom (below links)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Layout</Label>
                    <select value={editing.contacts_layout || 'grid'} onChange={e => set('contacts_layout', e.target.value)} className="w-full border rounded-lg px-2 h-8 text-sm bg-white" data-testid="contacts-layout-select">
                      <option value="grid">Card Grid</option>
                      <option value="list">Single Column List</option>
                    </select>
                  </div>
                </div>

                {(!editing.contacts || editing.contacts.length === 0) && (
                  <p className="text-xs text-gray-400 text-center py-6">No contacts. Click Add Contact to start.</p>
                )}
                <div className="space-y-2">
                  {editing.contacts?.map((c, idx) => {
                    const upd = (f, v) => set('contacts', editing.contacts.map(x => x.id === c.id ? { ...x, [f]: v } : x));
                    const move = (dir) => {
                      const arr = [...editing.contacts]; const i = idx; const s = dir === 'up' ? i - 1 : i + 1;
                      if (s < 0 || s >= arr.length) return; [arr[i], arr[s]] = [arr[s], arr[i]]; set('contacts', arr);
                    };
                    const photoSrc = c.photo?.startsWith('/') ? `${API}${c.photo}` : c.photo;
                    return (
                      <div
                        key={c.id}
                        onDragOver={(e) => dragOverIf(e, c.id, 'contact')}
                        onDrop={(e) => dropIf(e, c.id, 'contact', 'contacts')}
                        onDragLeave={() => { if (dropTargetId === c.id) setDropTargetId(null); }}
                        data-testid={`contact-row-${idx}`}
                        className={`border rounded-lg p-3 bg-gray-50/80 transition-all ${!c.enabled ? 'opacity-50' : ''} ${dragItemId === c.id && dragKind === 'contact' ? 'opacity-40 scale-[0.99]' : ''} ${dropTargetId === c.id && dragKind === 'contact' && dragItemId && dragItemId !== c.id ? 'ring-2 ring-blue-400 border-blue-300 bg-blue-50/60' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className="flex flex-col items-center"
                            draggable
                            onDragStart={(e) => startDrag(e, c.id, 'contact')}
                            onDragEnd={endDrag}
                            title="Drag to reorder"
                            data-testid={`contact-drag-handle-${idx}`}
                          >
                            <GripVertical className="w-4 h-4 text-gray-300 hover:text-gray-600 cursor-grab active:cursor-grabbing mb-0.5" />
                            <button onClick={() => move('up')} disabled={idx === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-20"><ChevronUp className="w-3 h-3" /></button>
                            <button onClick={() => move('down')} disabled={idx === editing.contacts.length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-20"><ChevronDown className="w-3 h-3" /></button>
                          </div>
                          <div className="shrink-0">
                            {c.photo ? (
                              <div className="relative group">
                                <img src={photoSrc} alt="" className="w-14 h-14 rounded-full object-cover border" />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 rounded-full flex items-center justify-center gap-1 transition-opacity">
                                  <label className="bg-white/90 rounded px-1.5 py-0.5 text-[10px] cursor-pointer">Replace
                                    <input type="file" accept="image/*" className="hidden" onChange={async e => { const f = e.target.files[0]; if (!f) return; try { const u = await uploadImage(f); upd('photo', u); toast.success('Photo updated'); } catch { toast.error('Upload failed'); } e.target.value = ''; }} />
                                  </label>
                                  <button onClick={() => upd('photo', '')} className="bg-white/90 rounded px-1.5 py-0.5 text-[10px] text-red-600">x</button>
                                </div>
                              </div>
                            ) : (
                              <label className="w-14 h-14 rounded-full border-2 border-dashed flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30">
                                <ImageIcon className="w-4 h-4 text-gray-400" />
                                <input type="file" accept="image/*" className="hidden" onChange={async e => { const f = e.target.files[0]; if (!f) return; try { const u = await uploadImage(f); upd('photo', u); toast.success('Photo uploaded'); } catch { toast.error('Upload failed'); } e.target.value = ''; }} />
                              </label>
                            )}
                          </div>
                          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Input className="h-8 text-sm font-medium" value={c.name || ''} onChange={e => upd('name', e.target.value)} placeholder="Name *" data-testid={`contact-name-${idx}`} />
                            <Input className="h-8 text-sm" value={c.role || ''} onChange={e => upd('role', e.target.value)} placeholder="Role / Title" />
                            <Input className="h-8 text-sm" value={c.email || ''} onChange={e => upd('email', e.target.value)} placeholder="Email" />
                            <Input className="h-8 text-sm" value={c.phone || ''} onChange={e => upd('phone', e.target.value)} placeholder="Phone" />
                            <Input className="h-8 text-sm sm:col-span-2" value={c.linkedin || ''} onChange={e => upd('linkedin', e.target.value)} placeholder="LinkedIn URL" />
                          </div>
                          <div className="flex flex-col gap-1">
                            <button onClick={() => upd('enabled', !c.enabled)} className={`p-1 rounded ${c.enabled ? 'text-green-500' : 'text-gray-300'}`} title="Enabled">{c.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                            <button onClick={() => set('contacts', editing.contacts.filter(x => x.id !== c.id))} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* ====== BLOCKS (Newsletter / HTML embeds) ====== */}
              <section className="bg-white rounded-xl border p-5 space-y-3" data-testid="blocks-section">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Blocks ({editing.blocks?.length || 0})</h2>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => addBlock('newsletter')} data-testid="add-newsletter-block-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Newsletter</Button>
                    <Button size="sm" variant="outline" onClick={() => addBlock('html')} data-testid="add-html-block-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Custom HTML</Button>
                  </div>
                </div>
                <p className="text-xs text-gray-500">Newsletter signup forms & custom HTML embeds render on the public page at their configured position.</p>
                {(!editing.blocks || editing.blocks.length === 0) && (
                  <p className="text-xs text-gray-400 text-center py-4">No blocks yet.</p>
                )}
                <div className="space-y-3">
                  {editing.blocks?.map((b, idx) => (
                    <div key={b.id} className={`border rounded-lg p-3 bg-gray-50/80 space-y-2 ${!b.enabled ? 'opacity-50' : ''}`} data-testid={`block-row-${idx}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                          {b.type === 'newsletter' ? 'Newsletter' : 'HTML'}
                        </span>
                        <select value={b.position || 'bottom'} onChange={e => updBlock(b.id, 'position', e.target.value)} className="text-xs border rounded px-2 h-7 bg-white" data-testid={`block-position-${idx}`}>
                          <option value="top">Position: Top</option>
                          <option value="bottom">Position: Bottom</option>
                        </select>
                        <div className="flex-1" />
                        <button onClick={() => updBlock(b.id, 'enabled', !b.enabled)} className={`p-1 rounded ${b.enabled ? 'text-green-500' : 'text-gray-300'}`} title="Enabled">{b.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                        <button onClick={() => rmBlock(b.id)} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      {b.type === 'newsletter' ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <Input className="h-8 text-xs" value={b.headline || ''} onChange={e => updBlock(b.id, 'headline', e.target.value)} placeholder="Headline" data-testid={`nl-headline-${idx}`} />
                            <Input className="h-8 text-xs" value={b.button_label || ''} onChange={e => updBlock(b.id, 'button_label', e.target.value)} placeholder="Button label" />
                          </div>
                          <Input className="h-8 text-xs" value={b.description || ''} onChange={e => updBlock(b.id, 'description', e.target.value)} placeholder="Description shown under headline" />
                          <Input className="h-8 text-xs" value={b.success_message || ''} onChange={e => updBlock(b.id, 'success_message', e.target.value)} placeholder="Success message" />
                          <div>
                            <Label className="text-[11px] text-gray-600">Add subscribers to contact list (optional)</Label>
                            <select className="w-full h-8 text-xs border rounded px-2 bg-white" value={b.add_to_list_id || ''} onChange={e => updBlock(b.id, 'add_to_list_id', e.target.value)} data-testid={`nl-list-${idx}`}>
                              <option value="">— Standalone (stored separately) —</option>
                              {contactLists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.contact_count || 0})</option>)}
                            </select>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Label className="text-[11px] text-gray-600">HTML content (rendered as-is)</Label>
                          <textarea className="w-full text-xs border rounded p-2 font-mono" rows={5} value={b.html || ''} onChange={e => updBlock(b.id, 'html', e.target.value)} data-testid={`html-content-${idx}`} />
                          <p className="text-[10px] text-gray-400 mt-1">Careful — HTML runs on the public page. Only paste trusted embed snippets (YouTube, Vimeo, Spotify, etc.).</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            </TabsContent>

            {/* ====== DESIGN ====== */}
            <TabsContent value="design" className="space-y-5">
              <section className="bg-white rounded-xl border p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Theme Presets</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.entries(PRESETS).map(([k, p]) => (
                    <button key={k} onClick={() => set('theme', { ...p })} className={`p-2.5 rounded-lg border-2 transition-all text-left ${editing.theme?.preset === k ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-400'}`}>
                      <div className="w-full h-6 rounded mb-1.5" style={{ background: `linear-gradient(135deg, ${p.gradient_start}, ${p.gradient_end})` }} />
                      <p className="text-[11px] font-medium">{p.label}</p>
                    </button>
                  ))}
                </div>
              </section>

              <section className="bg-white rounded-xl border p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Background</h2>
                <select value={editing.theme?.background_type || 'gradient'} onChange={e => set('theme.background_type', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="gradient">Gradient</option><option value="solid">Solid</option><option value="image">Image</option>
                </select>
                {editing.theme?.background_type === 'gradient' && (
                  <div className="grid grid-cols-2 gap-3">
                    <ColorPick label="Start" value={editing.theme?.gradient_start} onChange={v => set('theme.gradient_start', v)} />
                    <ColorPick label="End" value={editing.theme?.gradient_end} onChange={v => set('theme.gradient_end', v)} />
                  </div>
                )}
                {editing.theme?.background_type === 'solid' && <ColorPick label="Color" value={editing.theme?.background_color} onChange={v => set('theme.background_color', v)} />}
                {editing.theme?.background_type === 'image' && <div><Label className="text-xs">Image URL</Label><Input value={editing.theme?.background_image || ''} onChange={e => set('theme.background_image', e.target.value)} placeholder="https://..." /></div>}
              </section>

              <section className="bg-white rounded-xl border p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Card Style</h2>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Button Style</Label><select value={editing.theme?.button_style || 'glass'} onChange={e => set('theme.button_style', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm bg-white"><option value="glass">Glass</option><option value="solid">Solid</option><option value="outline">Outline</option></select></div>
                  <div><Label className="text-xs">Spacing: {editing.theme?.card_spacing || 16}px</Label><input type="range" min="8" max="32" value={editing.theme?.card_spacing || 16} onChange={e => set('theme.card_spacing', parseInt(e.target.value))} className="w-full mt-1" /></div>
                </div>
              </section>

              <section className="bg-white rounded-xl border p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Colors</h2>
                <div className="grid grid-cols-3 gap-3">
                  <ColorPick label="Accent" value={editing.theme?.accent_color} onChange={v => set('theme.accent_color', v)} />
                  <ColorPick label="Accent Bright" value={editing.theme?.accent_bright} onChange={v => set('theme.accent_bright', v)} />
                  <ColorPick label="Title" value={editing.theme?.title_color} onChange={v => set('theme.title_color', v)} />
                  <ColorPick label="Bio" value={editing.theme?.bio_color} onChange={v => set('theme.bio_color', v)} />
                  <ColorPick label="Link Title" value={editing.theme?.link_title_color} onChange={v => set('theme.link_title_color', v)} />
                  <ColorPick label="Link Subtitle" value={editing.theme?.link_subtitle_color} onChange={v => set('theme.link_subtitle_color', v)} />
                </div>
              </section>

              <section className="bg-white rounded-xl border p-5">
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-2"><Switch checked={editing.theme?.show_social_row !== false} onCheckedChange={v => set('theme.show_social_row', v)} /><Label className="text-xs">Social icons</Label></div>
                  <div className="flex items-center gap-2"><Switch checked={editing.theme?.show_grain_texture !== false} onCheckedChange={v => set('theme.show_grain_texture', v)} /><Label className="text-xs">Grain texture</Label></div>
                </div>
              </section>
            </TabsContent>

            {/* ====== SETTINGS ====== */}
            <TabsContent value="settings" className="space-y-5">
              <section className="bg-white rounded-xl border p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Publish</h2>
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-2"><Switch checked={editing.published} onCheckedChange={v => set('published', v)} /><Label className="text-xs">{editing.published ? 'Published (Live)' : 'Draft (Hidden)'}</Label></div>
                  <div className="flex items-center gap-2"><Switch checked={editing.noindex} onCheckedChange={v => set('noindex', v)} /><Label className="text-xs">Noindex</Label></div>
                </div>
              </section>
              <section className="bg-white rounded-xl border p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Custom Domain</h2>
                <Input value={editing.custom_domain || ''} onChange={e => set('custom_domain', e.target.value || null)} placeholder="links.yourdomain.com" />
                <p className="text-xs text-gray-400">CNAME your domain to this app to use it for this hub.</p>
              </section>
              <section className="bg-white rounded-xl border p-5 space-y-4">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Footer & Tracking</h2>
                <div><Label className="text-xs">Footer Text</Label><Input value={editing.footer_text || ''} onChange={e => set('footer_text', e.target.value)} placeholder="Your Brand Name" /></div>
                <div><Label className="text-xs">UTM Parameters</Label><Input value={editing.utm_params || ''} onChange={e => set('utm_params', e.target.value)} placeholder="utm_source=linkhub&utm_medium=social" /><p className="text-xs text-gray-400 mt-1">Auto-appended to all link URLs</p></div>
              </section>
            </TabsContent>
          </Tabs>
            </div>
          </div>

          {showPreview && (
            <aside className="admin-linkhub-preview-pane" data-testid="linkhub-preview-pane">
              <div className="admin-linkhub-preview-toolbar">
                <span className="admin-linkhub-preview-label">Live Preview</span>
                <div className="admin-linkhub-preview-devices">
                  <button onClick={() => setPreviewDevice('mobile')} className={previewDevice === 'mobile' ? 'active' : ''} title="Mobile" data-testid="preview-device-mobile"><Smartphone className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setPreviewDevice('tablet')} className={previewDevice === 'tablet' ? 'active' : ''} title="Tablet" data-testid="preview-device-tablet"><Tablet className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setPreviewDevice('desktop')} className={previewDevice === 'desktop' ? 'active' : ''} title="Desktop" data-testid="preview-device-desktop"><Monitor className="w-3.5 h-3.5" /></button>
                </div>
                <button onClick={() => setShowPreview(false)} className="admin-linkhub-preview-close" title="Close preview" data-testid="close-preview-btn"><X className="w-4 h-4" /></button>
              </div>
              <div className={`admin-linkhub-preview-frame device-${previewDevice}`}>
                <div className="admin-linkhub-preview-viewport">
                  <LinkHubRenderer hub={editing} slug={editing.slug || 'preview'} isPreview />
                </div>
              </div>
            </aside>
          )}
        </div>
      </AdminLayout>
    );
  }

  // ===== LIST VIEW =====
  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div><h1 className="text-xl font-bold text-gray-900">Link Hubs</h1><p className="text-xs text-gray-500 mt-0.5">Linktree-style link pages</p></div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={fetch_hubs}><RefreshCw className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant="outline" onClick={() => window.location.assign('/admin/link-hub/analytics')} data-testid="open-analytics-btn"><BarChart3 className="w-3.5 h-3.5 mr-1" /> Analytics</Button>
            <Button size="sm" onClick={() => { setEditing(newHub()); setTab('content'); }} className="bg-blue-900 hover:bg-blue-800 text-white" data-testid="create-hub-btn"><Plus className="w-3.5 h-3.5 mr-1" /> New Hub</Button>
          </div>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{[1, 2, 3].map(i => <div key={i} className="bg-white rounded-xl border p-5 animate-pulse h-36" />)}</div>
        ) : hubs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border">
            <Link2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-medium text-gray-700 mb-1">No Link Hubs</h3>
            <p className="text-xs text-gray-500 mb-4">Create your first link hub page.</p>
            <Button size="sm" onClick={() => { setEditing(newHub()); setTab('content'); }} className="bg-blue-900 hover:bg-blue-800 text-white"><Plus className="w-3.5 h-3.5 mr-1" /> Create</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {hubs.map(h => (
              <div key={h.id} data-testid={`hub-card-${h.slug}`} className="bg-white rounded-xl border shadow-sm hover:shadow-md transition-shadow p-4">
                <div className="flex items-start gap-3 mb-3">
                  {h.header?.avatar_image ? <img src={h.header.avatar_image.startsWith('/') ? `${API}${h.header.avatar_image}` : h.header.avatar_image} alt="" className="w-10 h-10 rounded-full object-cover border" />
                  : <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center text-white font-bold text-sm">{(h.title || 'L')[0].toUpperCase()}</div>}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm truncate">{h.title}</h3>
                    <p className="text-xs text-gray-500">/links/{h.slug}</p>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${h.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{h.published ? 'Live' : 'Draft'}</span>
                </div>
                <div className="flex gap-3 text-[11px] text-gray-500 mb-3">
                  <span><Link2 className="w-3 h-3 inline mr-0.5" />{h.links?.length || 0} links</span>
                  <span><Globe className="w-3 h-3 inline mr-0.5" />{h.social_links?.length || 0} social</span>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditing({ ...h }); setTab('content'); }} data-testid={`edit-hub-${h.slug}`}><Edit className="w-3 h-3 mr-1" />Edit</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => window.open(`/links/${h.slug}`, '_blank')}><ExternalLink className="w-3 h-3 mr-1" />View</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => window.location.assign(`/admin/link-hub/analytics?slug=${encodeURIComponent(h.slug)}`)} data-testid={`analytics-hub-${h.slug}`}><BarChart3 className="w-3 h-3 mr-1" />Stats</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 hover:bg-red-50" onClick={() => del(h)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminLinkHub;
